/**
 * Time buckets. Shared by the mock engine (grouping) and presenters (axis
 * labels) so a bucket's label can never disagree with the rows in it.
 * Ported unchanged from the Reef prototype's `data/buckets.ts`.
 */
import { addDays, startOfDay, startOfMonth } from '@deepdive-ui';

export type Bucket = 'day' | 'week' | 'month';

/** Monday-start weeks, matching the week×hour heatmap and the calendar. */
function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() + 6) % 7));
}

export function bucketStart(day: number, bucket: Bucket): number {
  const date = new Date(day);
  if (bucket === 'day') return startOfDay(date).getTime();
  if (bucket === 'week') return startOfWeek(date).getTime();
  return startOfMonth(date).getTime();
}

function nextBucket(start: Date, bucket: Bucket): Date {
  if (bucket === 'day') return addDays(start, 1);
  if (bucket === 'week') return addDays(start, 7);
  return new Date(start.getFullYear(), start.getMonth() + 1, 1);
}

/** Every bucket start from `from` to `to` inclusive. */
export function bucketsIn(from: number, to: number, bucket: Bucket): number[] {
  if (to < from) return [];
  const out: number[] = [];
  let cursor = new Date(bucketStart(from, bucket));
  const end = bucketStart(to, bucket);
  while (cursor.getTime() <= end) {
    out.push(cursor.getTime());
    cursor = nextBucket(cursor, bucket);
  }
  return out;
}

/** ≤70 days daily, ≤183 weekly, else monthly — the prototype's thresholds. */
export function autoBucket(from: number, to: number): Bucket {
  const days = Math.round((to - from) / 86_400_000) + 1;
  if (days <= 70) return 'day';
  if (days <= 183) return 'week';
  return 'month';
}

const dayFormat = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' });
const monthFormat = new Intl.DateTimeFormat('en-US', { month: 'short' });

export function bucketLabel(start: number, bucket: Bucket): string {
  const date = new Date(start);
  return bucket === 'month' ? monthFormat.format(date) : dayFormat.format(date);
}
